import PodcastGallery, {
  PodcastBHCustomEventCategory,
} from "@components/Gallery/PodcastGallery";
import VideoGallery from "@components/Gallery/VideoGallery";
import TranslationPopover from "@components/Translation/TranslationPopover";
import WordPuzzle from "@components/WordPuzzle/WordPuzzle";
import { pubPageAdTargeting } from "@elements/Advertisement/AdsConstants";
import { getTagsName } from "@elements/GAData/GADataHelper";
import { useBHVirtualPVAnalyticsCall } from "@hooks/useAnalyticsCall/useBHVirtualPVAnalyticsCall";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import { useGrapeshotData } from "@hooks/useGrapeshot";
import BHArticleBasePage, {
  AdsSettings,
  BHArticleBasePageProps,
} from "@pages/BHArticleBasePage";
import ErrorPage from "@pages/ErrorPage";
import NotFound from "@pages/NotFound";
import {
  CustomContext,
  TRouteWithRedirect,
  useRouteContext,
} from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import { useEffect, useState } from "react";

import { PayloadResponseType } from "./Article.server";
import ArticleContentCard from "./ArticleContentCard";
import ArticleInfiniteScroll from "./ArticleInfiniteScroll";
import ArticleMetaData from "./ArticleMetaData";

export function ArticlePage() {
  const routeContext: CustomContext<
    TRouteWithRedirect<PayloadResponseType, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const [currentArticle, setCurrentArticle] = useState<ProcessedArticleData>();
  const [pagination, setPagination] = useState<number>(0);
  const [resolvedGsChannels, setResolvedGsChannels] = useState<string[]>([]);
  const adsSettings: AdsSettings = useGlobalAdSetting();
  const { gsChannels, isLoading } = useGrapeshotData(
    dataLoaderResponse.type === ResponseType.SUCCESS
      ? (dataLoaderResponse.payload.article.urlPath as string)
      : "",
  );
  const analyticsCall = useBHVirtualPVAnalyticsCall();

  // update when gsChannels are loaded
  useEffect(() => {
    if (!isLoading && gsChannels.length) {
      setResolvedGsChannels(gsChannels);
    }
  }, [isLoading, gsChannels]);

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  if (dataLoaderResponse.type === ResponseType.SUCCESS) {
    const pageAdTargetValue = PageAdTargetingTypeEnum.ARTICLE;
    const PubPageAdTarget = pubPageAdTargeting[
      pageAdTargetValue
    ] as AdTargetingType<ArticleAdTargetKeys, string>;
    const mainArticle = dataLoaderResponse.payload.article;
    const latestRegisteredArticle =
      dataLoaderResponse.payload.latestRegisteredArticle;

    const handleNewArticle = (
      newArticle: ProcessedArticleData,
      pageNumber: number,
      viewPortChange: boolean,
    ) => {
      setCurrentArticle(newArticle);
      setPagination(pageNumber);
      if (viewPortChange) {
        setTimeout(() => {
          analyticsCall(
            newArticle.title,
            undefined,
            undefined,
            undefined,
            pageNumber,
          );
        }, 300);
      }
    };

    const { gaData, metaTags } = ArticleMetaData({
      article: currentArticle || mainArticle,
      pageNumber: pagination,
      gsChannels:
        currentArticle && currentArticle.gsChannels
          ? currentArticle.gsChannels
          : resolvedGsChannels,
    });

    const bhTags = getTagsName(mainArticle.tags || []);
    const bhArticleId = mainArticle.id;
    const articleSlotTarget: AdTargetingType<ArticleAdTargetKeys, string>[] = [
      { key: ArticleAdTargetKeys.ARTICLE_ID, value: bhArticleId },
      { key: ArticleAdTargetKeys.TAGS, value: bhTags.toLowerCase() },
      PubPageAdTarget,
    ];

    const adsUniqueSectionName = mainArticle.adsUniqueSectionName;

    const basePageProps: BHArticleBasePageProps = {
      adsUniqueSectionName,
      gaData,
      metaTags,
      suppliedAdsSettings: adsSettings,
      gsChannels: resolvedGsChannels,
      articleSlotTarget,
      prestitialPageTargeting: pageAdTargetValue,
    };

    return (
      <>
        <BHArticleBasePage {...basePageProps}>
          <div id={mainArticle.id} className="article-card">
            <ArticleContentCard
              mainArticle={mainArticle}
              latestRegisteredArticle={latestRegisteredArticle}
              pageNumber={0}
              isInfiniteScrollArticle={false}
              isOutbrainEnabled={adsSettings.isOutbrainEnabled || false}
              gsChannels={resolvedGsChannels}
              articleSlotTarget={articleSlotTarget}
              visibleArticleId={
                currentArticle ? currentArticle.id : mainArticle.id
              }
            />
          </div>
          <VideoGallery />
          <PodcastGallery
            bhCustomEventProps={{
              eventCategory: PodcastBHCustomEventCategory.ARTICLE_PAGE,
              eventAction: "click",
            }}
          />
          <ArticleInfiniteScroll
            mainArticle={mainArticle}
            latestRegisteredArticle={latestRegisteredArticle}
            onNewArticle={handleNewArticle}
            isOutbrainEnabled={adsSettings.isOutbrainEnabled || false}
            PubPageAdTarget={PubPageAdTarget}
          />
          <TranslationPopover
            gsChannels={resolvedGsChannels}
            articleSlotTarget={articleSlotTarget}
          />
        </BHArticleBasePage>
        <WordPuzzle />
      </>
    );
  }
}
